import { Button, Col, Form, Row, Flex } from "antd"
import { compressFile } from "helpers/compress"
import useFormErrors from "hooks/useAntdFormErrors"
import { useDevice } from "hooks/useDevice"
import { serialize } from "object-to-formdata"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { guideSelector, updateGuide, updateUser, sendOtpToVerifyEmail, verifyOtpForEmailUpdation, getUser } from "store/user"
import { updateStaffProfile } from "store/staff"
import { CustomForm } from "ui/Form"
import { CustomInput, TextArea } from "ui/Input"
import GooglePlacesNewInput from "modules/google/GooglePlacesNewInput"
import { LocationInfo } from "pagesV2/pro/Experiences/ListingLocation/LocationForm"
import { convertGoogleAddressComponentsToObj } from "modules/google/helpers"
import MapPinPicker from "ui/PersonalInfo/Addresses/MapPinPicker/MapPinPicker"
import { extractAddressFields } from "ui/PersonalInfo/Addresses/addressUtils"
import { ChangeEmailPhoneForm } from "../LoginAndSecurity/ChangeEmailPhoneForm"
import { AddAvatar } from "ui/PersonalInfo/ProfileInformation/AddAvatar"
import { notification } from "ui/Notification"
import styles from "./ProfileInformation.module.css"
import { Text, Title } from "ui/Typography"
import { Icon } from "ui/Icon"
import { colors } from "themes/variables"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { ProfileImages } from "pagesV2/pro/Settings/ProfileImages"
import { useAiTextDescription } from "hooks/useAiTextDescription"
import { handleNameKeyDown, handleFirstNamePaste, handleFirstNameInput } from "utils/InputSanitizers"
import { handleLastNameInput, handleLastNameKeyPress } from "pagesV2/pro/utils/ProUtils"
export const ProfileInformation = () => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const [isFieldsTouched, setIsFieldsTouched] = useState(false)
  const [qrImageError, setQrImageError] = useState(false)
  const { descriptionValue, setDescriptionValue, isShowAiModal, setIsShowAiModal, profileDescription, setProfileDescription } =
    useAiTextDescription()
  const {
    id,
    display_name,
    company_name,
    first_name,
    last_name,
    email,
    website,
    description,
    country,
    street_address,
    city,
    state,
    qr_code,
    qr_code_url,
    zip_code,
    radius,
    address_line_2,
    latitude,
    longitude
  } = useSelector(guideSelector)
  const {
    avatar,
    guide: { active },
    role,
    first_name: userFirstName,
    last_name: userLastName,
    email: userEmail,
    active_profile
  } = useSelector((store) => store.user)
  const isStaffUser = role === "staff_user"
  const staffFirstName = active_profile?.first_name || userFirstName
  const staffLastName = active_profile?.last_name || userLastName
  const staffEmail = active_profile?.email || userEmail
  const staffTitle = active_profile?.title || ""
  const staffDescription = active_profile?.description || ""
  const { loading } = useSelector((state) => state.auth)
  const { isDesktop } = useDevice()
  const navigate = useNavigate()
  const [emailEdited, setEmailEdited] = useState(false)
  const [currentEmail, setCurrentEmail] = useState("")
  const [otpSent, setOtpSent] = useState(false)
  const [otpCode, setOtpCode] = useState("")
  const [isSendingOtp, setIsSendingOtp] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [otpVerificationFailed, setOtpVerificationFailed] = useState(false)
  const [emailForOtp, setEmailForOtp] = useState("")

  const [addressText, setAddressText] = useState(null)
  const [manualAddress, setManualAddress] = useState(false)
  const [centerLatLng, setCenterLatLng] = useState(null)

  const mapLatitude = Form.useWatch("latitude", form)
  const mapLongitude = Form.useWatch("longitude", form)

  const handleMapLocationChange = useCallback(({ lat, lng, formattedAddress, addressComponents }) => {
    form.setFieldsValue({ latitude: lat, longitude: lng })
    setCenterLatLng({ lat, lng })
    if (formattedAddress) setAddressText(formattedAddress)
    if (addressComponents) {
      const fields = extractAddressFields(addressComponents)
      form.setFieldsValue(fields)
      if (!manualAddress) {
        form.setFieldsValue({ address_line_2: formattedAddress })
      }
    }
    setIsFieldsTouched(true)
  }, [form, manualAddress])
  const [profileImagesState, setProfileImagesState] = useState({ hasChanges: false, canSave: true })
  const profileImagesRef = useRef(null)

  const requiredErrorMessage = t("form_validation.cannot_be_blank")

  // Initialize address UI based on existing values
  useEffect(() => {
    if (street_address || city || state || zip_code || country) {
      setManualAddress(true)
      const addressStr = [street_address, city, state, zip_code, country].filter(Boolean).join(", ")
      if (addressStr) setAddressText(addressStr)
    }
  }, [street_address, city, state, zip_code, country])

  useEffect(() => {
    if (!manualAddress && address_line_2) {
      setAddressText(address_line_2)
      form.setFieldsValue({ address_line_2 })
    }
  }, [manualAddress, address_line_2, form])

  // Prefill address fields into main form when entering manual mode or when redux values change
  useEffect(() => {
    if (!manualAddress) return
    form.setFieldsValue({
      street_address: street_address || "",
      city: city || "",
      state: state || "",
      zip_code: zip_code || "",
      country: country || undefined
    })
  }, [manualAddress, street_address, city, state, zip_code, country, form])

  const placeChangedHandler = ({ target }) => {
    const { place, value, coordinates } = target || {}
    if (place?.address_components) {
      const config = {
        street_address: { elements: ["street_number", "route"], divider: " " },
        city: { elements: ["locality"] },
        state: { elements: ["administrative_area_level_1"] },
        zip_code: { elements: ["postal_code", "postal_code_suffix"], divider: "-" },
        country: { elements: ["country"] }
      }
      const obj = convertGoogleAddressComponentsToObj(place.address_components, config)
      form.setFieldsValue(obj)
      setIsFieldsTouched(true)
    }
    if (typeof coordinates?.lat === "number" && typeof coordinates?.lng === "number") {
      setCenterLatLng({ lat: coordinates.lat, lng: coordinates.lng })
      form.setFieldsValue({ latitude: coordinates.lat, longitude: coordinates.lng })
    }
    if (value !== undefined) {
      setAddressText(value)
      // keep form in sync so Form.Item required rule can validate correctly
      form.setFieldsValue({ address_line_2: value })
      // trigger validation and enable Save button
      form.validateFields(["address_line_2"])
      setIsFieldsTouched(true)
    }
  }

  const onFinish = (formData) => {
    const newFormData = {
      ...formData,
      longitude: centerLatLng?.lng,
      latitude: centerLatLng?.lat,
      address_line_2: formData?.address_line_2 ?? "",
      is_manual_address: Boolean(manualAddress)
    }
    const serializedFormData = serialize({ guide: newFormData }, { indices: true })
    return dispatch(updateGuide(id, serializedFormData)).then(() => {
      setIsFieldsTouched(false)
    })
  }

  const handleFieldsChange = () => {
    setIsFieldsTouched(true)
  }

  const handleNavigateBack = () => {
    navigate(-1)
  }

  const handleCancel = () => {
    handleNavigateBack()
  }

  const handleSaveAll = async () => {
    const tasks = [
      form
        .validateFields()
        .then((values) => onFinish(values))
        .catch((error) => {
          throw error
        }),
      profileImagesRef.current?.save?.({ showToast: false })
    ].filter(Boolean)

    const results = await Promise.allSettled(tasks)
    const hasRejected = results.some((result) => result.status === "rejected")

    if (hasRejected) {
      console.error(
        t("user.guide_update_error")
      ) //temporary log instead of notification to avoid showing multiple error toasts when both form and profile image update fails. We can change this logic later to show appropriate error messages based on which one failed.
    } else {
      dispatch(getUser())
      notification.success({
        message: t("user.guide_success_update")
      })
    }
  }

  const handleCancelAll = () => {
    profileImagesRef.current?.cancel()
    handleCancel()
  }

  const handleManualAddressConfirmed = () => {
    setIsFieldsTouched(true)
  }

  const handleImageChange = async (e) => {
    const avatar = await compressFile(e.target.files[0])
    const formData = serialize({ avatar })
    if (avatar) dispatch(updateUser(formData))
  }
  const downloadQR = () => {
    fetch(qr_code, {
      method: "GET",
      headers: {}
    })
      .then((response) => {
        response.arrayBuffer().then(function (buffer) {
          const url = window.URL.createObjectURL(new Blob([buffer]))
          const link = document.createElement("a")
          link.href = url
          link.setAttribute("download", "qr_code.png")
          document.body.appendChild(link)
          link.click()
        })
      })
      .catch((err) => {
        console.log(err)
      })
  }
  useFormErrors(form)

  useEffect(() => {
    form.setFieldsValue({ description: descriptionValue })
  }, [descriptionValue, form])

  useEffect(() => {
    const currentDescription = isStaffUser ? staffDescription : description
    if (currentDescription) {
      setDescriptionValue(currentDescription)
    }
  }, [isStaffUser, staffDescription, description, setDescriptionValue])

  useEffect(() => {
    const currentDescription = isStaffUser ? staffDescription : description
    setIsFieldsTouched(descriptionValue !== currentDescription)
  }, [isStaffUser, descriptionValue, staffDescription, description])

  useEffect(() => {
    if (isStaffUser) {
      setCurrentEmail(staffEmail)
    } else {
      setCurrentEmail(userEmail)
    }
  }, [isStaffUser, staffEmail, userEmail])

  useEffect(() => {
    if (otpSent && currentEmail !== emailForOtp) {
      setOtpSent(false)
      setOtpCode("")
      setOtpVerificationFailed(false)
      form.setFieldValue("otp_code", "")
    }
  }, [currentEmail, emailForOtp, otpSent, form])

  const descriptionLength = descriptionValue.length < 25

  const handleSendOtp = async () => {
    try {
      await form.validateFields(["email"])
      setIsSendingOtp(true)
      dispatch(sendOtpToVerifyEmail(currentEmail))
        .then((res) => {
          notification.success({
            message: res.message || t("settings.otp_sent")
          })
          setEmailForOtp(currentEmail)
          setOtpVerificationFailed(false)
          setOtpSent(true)
        })
        .catch(() => {})
        .finally(() => {
          setIsSendingOtp(false)
        })
    } catch (e) {}
  }

  const handleVerifyOtp = () => {
    setIsVerifyingOtp(true)
    const payload = {
      email: currentEmail,
      otp: otpCode
    }
    dispatch(verifyOtpForEmailUpdation(payload))
      .then(() => {
        notification.success({
          message: t("settings.email_confirmed")
        })
        setOtpSent(false)
        setEmailEdited(false)
        setOtpCode("")
        setEmailForOtp("")
        setOtpVerificationFailed(false)
        setIsFieldsTouched(false)
        form.setFieldValue("otp_code", "")
      })
      .catch(() => {
        setOtpVerificationFailed(true)
      })
      .finally(() => {
        setIsVerifyingOtp(false)
      })
  }

  const handleCancelEmailEdit = () => {
    form.setFieldsValue({ email: staffEmail, otp_code: "" })
    setEmailEdited(false)
    setCurrentEmail(staffEmail)
    setOtpSent(false)
    setOtpCode("")
    setEmailForOtp("")
    setOtpVerificationFailed(false)
  }

  const handleStaffSave = (values) => {
    const staffProfileId = active_profile?.id
    if (!staffProfileId) {
      notification.error({
        message: t("user.staff_profile_not_found", { defaultValue: "Staff profile not found" })
      })
      return
    }

    const payload = {
      profile: {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        title: values.title,
        description: values.description
      }
    }

    const serializedData = serialize(payload, { indices: true })
    dispatch(updateStaffProfile(staffProfileId, serializedData))
      .then(() => {
        setIsFieldsTouched(false)
        dispatch(getUser())
        notification.success({
          message: t("user.success_update")
        })
      })
      .catch((error) => {
        console.error("Failed to update staff profile:", error)
        notification.error({
          message: t("user.update_error", { defaultValue: "Failed to update profile" })
        })
      })
  }

  if (isStaffUser) {
    return (
      <div className={styles.profileInfoNarrow}>
        <CustomForm
          form={form}
          layout="vertical"
          initialValues={{
            first_name: staffFirstName,
            last_name: staffLastName,
            email: staffEmail,
            title: staffTitle,
            description: staffDescription
          }}
          onFinish={handleStaffSave}
          onValuesChange={() => setIsFieldsTouched(true)}
        >
          <Row gutter={12}>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label={t("sign_up.first_name")}
                name="first_name"
                required={true}
                rules={[{ required: true, message: requiredErrorMessage }]}
              >
                <CustomInput placeholder={t("sign_up.first_name")} />
              </Form.Item>
            </Col>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label={t("sign_up.last_name")}
                name="last_name"
                required={true}
                rules={[{ required: true, message: requiredErrorMessage }]}
              >
                <CustomInput placeholder={t("sign_up.last_name")} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col md={12} sm={24} xs={24}>
              <Form.Item label={t("user.title")} name="title">
                <CustomInput placeholder={t("user.title")} />
              </Form.Item>
            </Col>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label={t("user.email")}
                name="email"
                required={emailEdited}
                rules={
                  emailEdited
                    ? [
                        { required: true, message: requiredErrorMessage },
                        { type: "email", message: t("form_validation.invalid_email") }
                      ]
                    : []
                }
              >
                <CustomInput
                  placeholder={t("user.email")}
                  onChange={(e) => {
                    const newEmail = e.target.value
                    setEmailEdited(newEmail !== staffEmail)
                    setCurrentEmail(newEmail)
                  }}
                />
              </Form.Item>
              {emailEdited && !otpSent && (
                <Flex gap="small" className="mt-xs">
                  <Button color="primary" variant="solid" onClick={handleSendOtp} loading={isSendingOtp} disabled={isSendingOtp}>
                    {t("user.send_otp", { defaultValue: "Send OTP" })}
                  </Button>
                  <Button variant="outlined" onClick={handleCancelEmailEdit}>
                    {t("global.cancel")}
                  </Button>
                </Flex>
              )}
              {otpSent && (
                <>
                  <Form.Item
                    label={t("enter_code.title")}
                    name="otp_code"
                    required
                    className="mt-m"
                    rules={[
                      { required: true, message: t("form_validation.cannot_be_blank") },
                      { len: 5, message: t("form_validation.five_digit_code") }
                    ]}
                  >
                    <CustomInput placeholder="XXXXX" maxLength={5} onChange={(e) => setOtpCode(e.target.value)} />
                  </Form.Item>
                  <Flex gap="small" className="mt-xs">
                    <Button
                      type="primary"
                      onClick={handleVerifyOtp}
                      loading={isVerifyingOtp}
                      disabled={otpCode.length !== 5 || isVerifyingOtp}
                    >
                      {t("global.confirm")}
                    </Button>
                    {otpVerificationFailed && (
                      <Button color="primary" variant="outlined" onClick={handleSendOtp} loading={isSendingOtp} disabled={isSendingOtp}>
                        {t("user.resend_otp", { defaultValue: "Resend OTP" })}
                      </Button>
                    )}
                    <Button variant="outlined" onClick={handleCancelEmailEdit}>
                      {t("global.cancel")}
                    </Button>
                  </Flex>
                </>
              )}
            </Col>
          </Row>
          <Row gutter={12}>
            <Col md={12} sm={24} xs={24}>
              <ChangeEmailPhoneForm inputType="phone_number" hideActions />
            </Col>
          </Row>
          <Form.Item label={t("guide.description")} name="description">
            <TextArea placeholder={t("guide.description_placeholder")} style={{ height: 147 }} />
          </Form.Item>
          <div className="flex gap-s mt-4xl mb-4xl">
            <Button variant="outlined" color="primary" size="large" style={{ width: isDesktop ? "auto" : "50%" }} onClick={handleCancel}>
              {t("global.cancel")}
            </Button>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              style={{ width: isDesktop ? "auto" : "50%" }}
              disabled={!isFieldsTouched || loading}
            >
              {t("settings.update")}
            </Button>
          </div>
        </CustomForm>
      </div>
    )
  }

  return (
    <>
      <div className={styles.profileInfoNarrow}>
        <CustomForm layout="vertical">
          <Form.Item name="avatarPhoto" label={t("guide.profile_image")} required={true} className="mb-4xl  relative w-100 ">
            <AddAvatar avatar={avatar} handleImageChange={handleImageChange} />
            {qr_code && (
              <Col xs={4} sm={4} className="text-center absolute top-20 right-0 avtarWrapper">
                <img src={qr_code} className="w-100" alt="" onError={() => setQrImageError(true)} onLoad={() => setQrImageError(false)} />
                {!qrImageError && (
                  <Button className="mx-auto" color="primary-second" outline onClick={downloadQR}>
                    Download
                  </Button>
                )}
              </Col>
            )}
          </Form.Item>
        </CustomForm>
        <CustomForm
          form={form}
          name="settings-profile-information"
          layout="vertical"
          initialValues={{
            display_name,
            company_name,
            email,
            website,
            qr_code_url,
            description,
            user_attributes: {
              first_name,
              last_name
            },
            radius,
            latitude: latitude ?? null,
            longitude: longitude ?? null,
            street_address: street_address || "",
            city: city || "",
            state: state || "",
            zip_code: zip_code || "",
            country: country || undefined
          }}
          onFinish={onFinish}
          onValuesChange={() => setIsFieldsTouched(true)}
        >
          <Row gutter={12}>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label={t("guide.display_name")}
                name="display_name"
                required={true}
                rules={[{ required: true, message: requiredErrorMessage }]}
              >
                <CustomInput
                  placeholder={t("guide.display_name")}
                  onChange={handleFieldsChange}
                  onKeyDown={handleLastNameKeyPress}
                  onInput={handleLastNameInput}
                  onBlur={(e) => form.setFieldsValue({ display_name: e.target.value.trim() })}
                />
              </Form.Item>
            </Col>
            <Col md={12} sm={24} xs={24}>
              <Form.Item label={t("guide.company_name")} name="company_name">
                <CustomInput
                  placeholder={t("guide.company_name")}
                  onChange={handleFieldsChange}
                  onKeyDown={handleLastNameKeyPress}
                  onInput={handleLastNameInput}
                  onBlur={(e) => form.setFieldsValue({ company_name: e.target.value.trim() })}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label={t("sign_up.first_name")}
                name={["user_attributes", "first_name"]}
                required={true}
                rules={[{ required: true, message: requiredErrorMessage }]}
              >
                <CustomInput
                  placeholder={t("sign_up.first_name")}
                  onChange={handleFieldsChange}
                  onKeyDown={handleNameKeyDown}
                  onInput={handleFirstNameInput}
                  onPaste={(e) => handleFirstNamePaste(e, (text) => form.setFieldsValue({ user_attributes: { ...form.getFieldValue("user_attributes"), first_name: text } }))}
                />
              </Form.Item>
            </Col>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label={t("sign_up.last_name")}
                name={["user_attributes", "last_name"]}
                required={true}
                rules={[{ required: true, message: requiredErrorMessage }]}
              >
                <CustomInput
                  placeholder={t("sign_up.last_name")}
                  onChange={handleFieldsChange}
                  onKeyDown={handleLastNameKeyPress}
                  onInput={handleLastNameInput}
                  onBlur={(e) => form.setFieldsValue({ user_attributes: { ...form.getFieldValue("user_attributes"), last_name: e.target.value.trim() } })}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label={t("user.email")}
                name="email"
                required={true}
                rules={[
                  { required: true, message: requiredErrorMessage },
                  { type: "email", message: t("form_validation.invalid_email") }
                ]}
              >
                <CustomInput placeholder={t("user.email")} onChange={handleFieldsChange} disabled />
              </Form.Item>
            </Col>
            <Col md={12} sm={24} xs={24}>
              <ChangeEmailPhoneForm inputType="phone_number" />
            </Col>
          </Row>

          <Row gutter={12}>
            <Col md={12} sm={24} xs={24}>
              <Form.Item label={t("guide.website")} name="website">
                <CustomInput placeholder="https://" onChange={handleFieldsChange} />
              </Form.Item>
            </Col>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                label={t("guide.qr_code_url", { defaultValue: "QR Code URL" })}
                name="qr_code_url"
                rules={[{ type: "url", message: t("form_validation.invalid_url", { defaultValue: "Please enter a valid URL" }) }]}
              >
                <CustomInput placeholder="https://" onChange={handleFieldsChange} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="description"
            required={true}
            rules={[{ required: true, message: t("pro.ai_enhancement.description_too_short") }]}
          >
            <Flex justify="space-between" align="center" className="mb-s w-full">
              <Text className={styles.descriptionLabel}>{t("guide.description")}*</Text>
              <Button
                color="primary"
                variant="solid"
                icon={<Icon name="aiEnhancement" color={descriptionLength ? colors.textPrimary : colors.disabled} />}
                disabled={!active || descriptionLength}
                onClick={active && !descriptionLength ? () => setIsShowAiModal(true) : undefined}
              >
                {t("pro.ai_enhancement.button_text")}
              </Button>
            </Flex>
            <TextArea
              placeholder={t("guide.description_placeholder")}
              style={{ height: 147 }}
              onChange={(e) => {
                setDescriptionValue(e.target.value)
              }}
              value={descriptionValue}
            />
          </Form.Item>

          {manualAddress ? (
            <Form.Item>
              <LocationInfo
                isRequired={true}
                screenName="Profile"
                backBtnManualText={t("client.back_to_manual_address")}
                form={form}
                t={t}
                setCenterLatLng={(coords) => {
                  setCenterLatLng(coords)
                  form.setFieldsValue({ latitude: coords?.lat ?? null, longitude: coords?.lng ?? null })
                }}
                setManualAddress={setManualAddress}
                setMeetingLatLng={setAddressText}
                onAddressConfirmed={handleManualAddressConfirmed}
              />
            </Form.Item>
          ) : (
            <Form.Item
              label={t("pro.addresses.street_address")}
              name="address_line_2"
              rules={[{ required: true, message: requiredErrorMessage }]}
            >
              <GooglePlacesNewInput
                placeholder={t("pro.addresses.street_address")}
                onChange={placeChangedHandler}
                meetingLatLng={addressText}
                setManualAddress={setManualAddress}
                setCenterLatLng={(coords) => {
                  setCenterLatLng(coords)
                  if (!coords) form.setFieldsValue({ latitude: null, longitude: null })
                }}
                setMeetingLatLng={setAddressText}
              />
            </Form.Item>
          )}

          <Form.Item name="latitude" hidden><CustomInput /></Form.Item>
          <Form.Item name="longitude" hidden><CustomInput /></Form.Item>

          <div className={styles.mapWrapper}>
            <MapPinPicker
              latitude={mapLatitude}
              longitude={mapLongitude}
              onLocationChange={handleMapLocationChange}
            />
          </div>

          <Form.Item label={t("pro.addresses.radius")} name="radius">
            <CustomInput placeholder={t("pro.addresses.radius")} onChange={handleFieldsChange} />
          </Form.Item>
        </CustomForm>
      </div>
      {isShowAiModal && (
        <AiEnhancementModal
          open={isShowAiModal}
          onCancel={() => {
            setIsShowAiModal(false)
            setProfileDescription("")
          }}
          profileDescription={profileDescription}
          description={descriptionValue}
          setDescription={setDescriptionValue}
          setProfileDescription={setProfileDescription}
          onOk={() => {
            setIsShowAiModal(false)
          }}
          text={t("pro.ai_enhancement.form.textarea_label")}
          modalTitle={t("pro.ai_enhancement.modal_title")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("pro.ai_enhancement.form.continue_refinement_details")}
          improveWithAITooltipTitle={t("pro.ai_enhancement.form.ai_info")}
        />
      )}
      <div className={`mt-6xl ${styles.profileImagesWide}`}>
        <Title level={3} className="mb-l">
          {t("profile_image.title", { defaultValue: "Business Profile Images" })}
        </Title>
        <ProfileImages ref={profileImagesRef} showActions={false} onStateChange={setProfileImagesState} />
      </div>
      <div className={styles.profileInfoNarrow}>
        <div className="flex gap-s mt-4xl mb-4xl">
          <Button
            variant="outlined"
            color="primary"
            size="large"
            style={{ width: isDesktop ? "auto" : "50%" }}
            onClick={active ? handleCancelAll : undefined}
            disabled={!active}
          >
            {t("global.cancel")}
          </Button>
          <Button
            className="mb-xs"
            type="primary"
            size="large"
            onClick={active ? handleSaveAll : undefined}
            style={{ width: isDesktop ? "auto" : "50%" }}
            disabled={
              !active ||
              loading ||
              (!isFieldsTouched && !profileImagesState.hasChanges) ||
              (profileImagesState.hasChanges && !profileImagesState.canSave)
            }
          >
            {t("settings.update")}
          </Button>
        </div>
      </div>
    </>
  )
}
